import React, { useEffect, useState } from "react";
import { Loader2, Plus, ArrowUp, ArrowDown, Save, Check } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const input = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);

export default function LeadSourceManager() {
  const { toast } = useToast();
  const [options, setOptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.leadSources.get().then((d) => setOptions(d.options || [])).catch(() => setOptions([]));
  }, []);

  const set = (i, patch) => {
    setOptions((o) => o.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
    setSaved(false);
  };
  const setDetail = (i, patch) => set(i, { detail: { ...(options[i].detail || {}), ...patch } });

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const next = [...options];
    [next[i], next[j]] = [next[j], next[i]];
    setOptions(next);
    setSaved(false);
  };

  const add = () => {
    setOptions([...options, { id: `option_${Date.now().toString(36)}`, label: "", active: true,
                              detail: { enabled: false, label: "", required: false } }]);
    setSaved(false);
  };

  const save = async () => {
    const clean = options.filter((o) => (o.label || "").trim())
      .map((o) => ({ ...o, id: o.id || slug(o.label) }));
    if (!clean.length) return toast({ title: "Give at least one option a name", variant: "destructive" });
    setSaving(true);
    try {
      const d = await adminApi.leadSources.save(clean);
      setOptions(d.options || clean);
      setSaved(true);
      toast({ title: "Saved — every form updated" });
    } catch {
      toast({ title: "Couldn't save that", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!options) return <p className="text-sm text-slate-400">Loading the options…</p>;

  return (
    <div data-testid="lead-source-manager">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h2 className="font-display font-bold text-navy">How did you hear about us?</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
          One question, asked the same way on the pricing tool, the guide downloads, the Discovery Call box and the
          contact form. Changes appear on every form straight away. Each option keeps a fixed identifier behind its
          wording and every submission stores the wording the visitor saw, so rewording an option never rewrites an
          old report. Switching an option off removes it from new forms only.
        </p>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={save} disabled={saving} data-testid="lead-source-save" className="bg-orange hover:bg-orange-dark text-white rounded-full h-10 text-xs">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : saved ? <Check size={14} className="mr-1.5" /> : <Save size={14} className="mr-1.5" />}
            {saved ? "Saved" : "Save the list"}
          </Button>
          <Button onClick={add} variant="outline" className="rounded-full h-10 text-xs" data-testid="lead-source-add">
            <Plus size={14} className="mr-1.5" /> Add an option
          </Button>
          <span className="text-xs text-slate-400">{options.filter((o) => o.active !== false).length} shown to visitors</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed max-w-3xl" data-testid="lead-source-calendly-note">
          <strong className="text-navy">One manual step in Calendly.</strong> For the answer to appear on the booking
          itself, the Discovery Call event type needs a single-line custom question — "How did you hear about us?" —
          and it must be the <strong>first</strong> custom question on that event type, because Calendly matches
          prefilled answers by position. Prefill also only works on an event-type link, not a general profile link.
          Either way the answer is still recorded in the report below.
        </p>
      </div>

      <div className="space-y-3">
        {options.map((o, i) => (
          <div key={o.id || i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`lead-source-row-${o.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-300 hover:text-navy disabled:opacity-30" aria-label="Move up" data-testid={`lead-source-up-${o.id}`}><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === options.length - 1} className="text-slate-300 hover:text-navy disabled:opacity-30" aria-label="Move down" data-testid={`lead-source-down-${o.id}`}><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <input
                  value={o.label || ""}
                  placeholder="What the visitor reads"
                  onChange={(e) => set(i, { label: e.target.value })}
                  data-testid={`lead-source-label-${o.id}`}
                  className={input}
                />
                <p className="text-[11px] text-slate-400 mt-1">Identifier: {o.id}</p>
              </div>
              <button
                onClick={() => set(i, { active: o.active === false })}
                data-testid={`lead-source-active-${o.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  o.active === false ? "bg-white text-slate-400 border-slate-200" : "bg-teal text-white border-teal"
                }`}
              >
                {o.active === false ? "Off" : "Shown"}
              </button>
            </div>

            <div className="mt-3 pl-8 rounded-xl bg-slate-50/70 border border-slate-100 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!o.detail?.enabled}
                  onChange={(e) => setDetail(i, { enabled: e.target.checked })}
                  data-testid={`lead-source-detail-on-${o.id}`}
                  className="accent-orange"
                />
                <span className="text-xs font-semibold text-navy">Ask a follow-up question</span>
              </label>
              {o.detail?.enabled && (
                <div className="grid md:grid-cols-[1fr_auto] gap-3 mt-3 items-center">
                  <input
                    value={o.detail?.label || ""}
                    placeholder="Which event?"
                    onChange={(e) => setDetail(i, { label: e.target.value })}
                    data-testid={`lead-source-detail-label-${o.id}`}
                    className={input}
                  />
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={!!o.detail?.required}
                      onChange={(e) => setDetail(i, { required: e.target.checked })}
                      data-testid={`lead-source-detail-required-${o.id}`}
                      className="accent-orange"
                    />
                    <span className="text-xs text-slate-600">Must be answered</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
